import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const privacyFields = [
  'sales_tax_id',
  'ein',
  'username',
  'password',
  'password_metadata',
  'routing_number',
  'account_number',
  'pin',
  'cdtfa_third_party_access_security_code',
  'ssn',
  'dl',
] as const;

export type PrivacyType = (typeof privacyFields)[number];

// eslint-disable-next-line react-refresh/only-export-components
export const isPrivacyField = (fieldName: string): fieldName is PrivacyType => {
  if (privacyFields.includes(fieldName as PrivacyType)) {
    return true;
  }
  return fieldName.endsWith('_ssn') || fieldName.endsWith('_dl');
};

interface PrivacyContextType {
  isPrivacyVisible: boolean;
  hasPrivacyOnPage: boolean;
  togglePrivacyVisibility: () => void;
  registerPrivacy: () => void;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

interface PrivacyProviderProps {
  children: ReactNode;
}

export const PrivacyProvider: React.FC<PrivacyProviderProps> = ({ children }) => {
  const [isPrivacyVisible, setIsPrivacyVisible] = useState(false);
  const [hasPrivacyOnPage, setHasPrivacyOnPage] = useState(false);
  const location = useLocation();

  // Reset privacy presence and visibility when navigating to a new page
  useEffect(() => {
    setHasPrivacyOnPage(false);
    setIsPrivacyVisible(false);
  }, [location.pathname]);

  const togglePrivacyVisibility = () => {
    setIsPrivacyVisible(!isPrivacyVisible);
  };

  const registerPrivacy = () => {
    setHasPrivacyOnPage(true);
  };

  return (
    <PrivacyContext.Provider
      value={{
        isPrivacyVisible,
        hasPrivacyOnPage,
        togglePrivacyVisibility,
        registerPrivacy,
      }}
    >
      {children}
    </PrivacyContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePrivacy = (): PrivacyContextType => {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
};
